import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import SiteHeader from "../components/SiteHeader";
import api, { formatApiErrorDetail } from "../lib/api";
import { KeyRound, ArrowRight, ShieldAlert } from "lucide-react";

export default function ChangePassword() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const forced = !!user?.must_change_password;

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) return setError("New password must be at least 8 characters.");
    if (next !== confirm) return setError("New password and confirmation do not match.");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      await refresh();
      navigate("/app", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <SiteHeader />
      <main className="max-w-md mx-auto px-6 py-16">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Account · Security</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-2 flex items-center gap-3">
          <KeyRound className="w-6 h-6 text-[#0F172A]" /> {forced ? "Set a new password" : "Change password"}
        </h1>
        {forced && (
          <div className="mt-4 border border-amber-500 bg-amber-50 text-amber-900 px-4 py-3 rounded-sm text-sm flex items-start gap-2" data-testid="force-change-banner">
            <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              For your security, please replace the temporary password before continuing. You'll be signed in as usual once you set a new one.
            </div>
          </div>
        )}

        <form onSubmit={submit} className="mt-6 space-y-5 border border-zinc-200 bg-white rounded-sm p-6">
          <div className="space-y-1.5">
            <Label htmlFor="current" className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              {forced ? "Temporary password" : "Current password"}
            </Label>
            <Input id="current" type="password" required value={current} onChange={(e) => setCurrent(e.target.value)}
                   className="rounded-sm h-10" data-testid="change-current-input" autoComplete="current-password" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new" className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">New password (min 8 chars)</Label>
            <Input id="new" type="password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)}
                   className="rounded-sm h-10" data-testid="change-new-input" autoComplete="new-password" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm" className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Confirm new password</Label>
            <Input id="confirm" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                   className="rounded-sm h-10" data-testid="change-confirm-input" autoComplete="new-password" />
          </div>
          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm px-3 py-2" data-testid="change-error">{error}</div>}
          <Button type="submit" disabled={loading} className="w-full bg-[#0F172A] hover:bg-[#0F172A]/90 rounded-sm h-10" data-testid="change-submit-btn">
            {loading ? "Updating…" : (<>Update password <ArrowRight className="w-4 h-4" /></>)}
          </Button>
        </form>

        {forced && (
          <button
            onClick={async () => { await logout(); navigate("/login"); }}
            className="mt-4 text-xs text-zinc-500 hover:text-zinc-800 underline underline-offset-4"
            data-testid="force-change-logout"
          >
            Sign out instead
          </button>
        )}
      </main>
    </div>
  );
}
